"use client";

import React, { useEffect, useState, useMemo } from "react";
import {
  Table,
  Button,
  Space,
  Popconfirm,
  Pagination,
  Switch,
  Breadcrumb,
  Typography,
} from "antd";
import { HomeOutlined, DeleteOutlined, EditOutlined } from "@ant-design/icons";
import { theme } from "antd";
import { apiRequest } from "../../../../../service/apiRequest";
import { showSuccess, showError } from "../../../../../components/errorHandler";
import { useCurrentModule } from "../../../../../hooks/useCurrentModule";
import TimberIssueMasterModal from "./TimberIssueMasterModal";
import { useAppDispatch, useAppSelector } from "../../../../../redux/hooks";
import { Select } from "antd";
import dayjs from "dayjs";
const { Text } = Typography;

/* ---------------- TYPES ---------------- */
export interface Employee {
  employee_id: number;
  employee_lname: string;
  employee_fname: string;
  employee_mobile: string;
}
export interface Product {
  product_id: number;
  product_name: string;
  last_purchase_price: number;
}
export interface PO {
  pbill_id: number;
  pbill_bill_no: string;
}

export interface WorkOrder {
  wo_id: number;
  product_name: string;
  wo_pi_id?: number | null;
  wo_number?: string | null;
  wo_employee_id?: number | null;
  employee_id?: number | null;
  employee_fname?: string | null;
  employee_lname?: string | null;
  department_id?: number | null;
  wo_department_id?: number | null;
  department_name?: string | null;
  wo_department_name?: string | null;
  isMachinery?: boolean;
}

export interface UnitType {
  ut_id: number;
  ut_name: string;
}
export interface IssueItem {
  key: string;
  product_id?: number;
  unit_id?: number;
  qty: number;
  price: number;
  total: number;
}
export interface IssueMasterData {
  im_id: number;
  im_issue_to: string;
  im_type: string;
  im_master_order_no: string;
  im_master_pi_no: string;
  im_status: number;
  employee_name: string;
  pi_order_no: string;
  employee_id: number;
  im_created_on: string;
  created_by?: string;

  // Optional relational fields from backend
  po?: {
    pbill_bill_no: string;
  };
  work_order?: {
    work_order_no: string;
  };
  im_total?: number;
}

/* ---------------- COMPONENT ---------------- */

const TimberIssueMaster: React.FC = () => {
  const { employees, loaded } = useAppSelector((state) => state.master);
  console.log("employees", employees);
  const [data, setData] = useState<IssueMasterData[]>([]);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [initialValues, setInitialValues] = useState<IssueMasterData | null>(
    null,
  );
  const [pagination, setPagination] = useState({ current: 1, pageSize: 10 });
  const [selectedEmployee, setSelectedEmployee] = useState<number | null>(null);

  const currentModule = useCurrentModule();
  const {
    token: { colorBgContainer, borderRadiusLG },
  } = theme.useToken();

  /* ---------------- FETCH DATA ---------------- */

  const fetchData = async (empId?: number) => {
    try {
      const url = empId
        ? `issue-master?type=Timber&employee_id=${empId}`
        : "issue-master?type=Timber";

      const res = await apiRequest<IssueMasterData[]>({
        method: "get",
        url,
      });

      // Sort latest first (by im_created_on timestamp or im_id descending)
      const sorted = (res || []).sort((a, b) => {
        const timeA = a.im_created_on ? new Date(a.im_created_on).getTime() : Number(a.im_id || 0);
        const timeB = b.im_created_on ? new Date(b.im_created_on).getTime() : Number(b.im_id || 0);
        return timeB - timeA;
      });

      setData(sorted);
    } catch {
      showError("Failed to fetch Issue Master data");
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  /* ---------------- MODAL HANDLERS ---------------- */

  const handleCreate = () => fetchData();

  const showCreateModal = () => {
    setIsEditMode(false);
    setInitialValues(null);
    setIsModalVisible(true);
  };

  const showEditModal = async (record: IssueMasterData) => {
    try {
      const response = await apiRequest<any>({
        method: "get",
        url: `issue-master?id=${record.im_id}`
      });
      if (!response) {
        showError("Failed to load issue details");
        return;
      }
      setIsEditMode(true);
      setInitialValues(response);
      setIsModalVisible(true);
    } catch {
      showError("Error loading issue details");
    }
  };

  const hideModal = () => {
    setIsModalVisible(false);
    setIsEditMode(false);
    setInitialValues(null);
  };

  /* ---------------- DELETE ---------------- */

  const handleDelete = async (id: number) => {
    try {
      await apiRequest({
        method: "delete",
        url: `issue-delete/${id}`,
      });
      showSuccess("Timber issue master deleted successfully");
      fetchData();
    } catch {
      showError("Failed to delete Timber issue master");
    }
  };

  /* ---------------- STATUS TOGGLE ---------------- */

  const handleStatusToggle = async (id: number) => {
    // Optimistic UI update
    setData((prev) =>
      prev.map((item) =>
        item.im_id === id
          ? { ...item, im_status: item.im_status === 1 ? 0 : 1 }
          : item,
      ),
    );

    try {
      await apiRequest({
        method: "put",
        url: `issue-status-toggle/${id}`,
      });
      showSuccess("Status updated");
    } catch {
      showError("Failed to update status");
      fetchData(); // revert on failure
    }
  };

  /* ---------------- TABLE COLUMNS ---------------- */

  const columns = [
    {
      title: "Issue To",
      dataIndex: "employee_name",
      key: "employee_name",
      render: (val: string) => val || "-",
    },
    {
      title: "PI Number",
      key: "pi_order_no",
      render: (_: any, record: IssueMasterData) =>
        record.pi_order_no
          ? `${record.pi_order_no} (${record.im_master_pi_no || 0})`
          : "-",
    },
    {
      title: "Work Order No",
      key: "work_order",
      render: (_: any, record: IssueMasterData) =>
        record.im_master_order_no || "-",
    },
    {
      title: "Grand Total",
      dataIndex: "im_total",
      key: "im_total",
      render: (val?: number) => (
        <Text strong>{val ? val.toFixed(4) : "0.00"}</Text>
      ),
    },
    {
      title: "Issue By / Date",
      key: "created_by",
      render: (_: any, record: IssueMasterData) => (
        <div>
          <div><Text strong>{record.created_by || "-"}</Text></div>
          {record.im_created_on && (
            <div style={{ fontSize: 12, color: "#64748b", marginTop: 2 }}>
              {dayjs(record.im_created_on).format("DD-MMM-YYYY hh:mm A")}
            </div>
          )}
        </div>
      ),
    },
    {
      title: "Status",
      dataIndex: "im_status",
      key: "im_status",
      render: (_: any, record: IssueMasterData) => (
        <Switch
          checked={record.im_status === 1}
          onChange={() => handleStatusToggle(record.im_id)}
        />
      ),
    },
  ];

  /* ---------------- ACTION COLUMN ---------------- */

  if (
    currentModule?.right_edit === "Y" ||
    currentModule?.right_delete === "Y"
  ) {
    columns.push({
      title: "Action",
      key: "action",
      dataIndex: "im_id", // ✅ REQUIRED for TS
      render: (_, record) => (
        <Space>
          <Button onClick={() => showEditModal(record)}>
            <EditOutlined />
          </Button>

          <Popconfirm
            title="Delete?"
            onConfirm={() => handleDelete(record.im_id)}
          >
            <Button danger>
              <DeleteOutlined />
            </Button>
          </Popconfirm>
        </Space>
      ),
    });
  }

  const grandTotalAllTimber = useMemo(() => {
    return data.reduce((sum, item) => sum + (Number(item.im_total) || 0), 0);
  }, [data]);

  /* ---------------- RENDER ---------------- */

  return (
    <>
      <Space
        align="center"
        style={{
          width: "100%",
          justifyContent: "space-between",
          marginBottom: 16,
        }}
      >
        <Breadcrumb>
          <Breadcrumb.Item href="/">
            <HomeOutlined />
          </Breadcrumb.Item>
          <Breadcrumb.Item>Timber Issue Master</Breadcrumb.Item>
        </Breadcrumb>

        <Space align="center">
          <Select
            showSearch
            optionFilterProp="label"
            style={{ width: 250 }}
            allowClear
            placeholder="Filter by Employee"
            value={selectedEmployee}
            onChange={(value) => {
              setSelectedEmployee(value);
              fetchData(value);
            }}
          >
            {employees.map((emp) => (
              <Select.Option
                key={emp.employee_id}
                value={emp.employee_id}
                label={`${emp.employee_fname} ${emp.employee_lname}`}
              >
                {emp.employee_fname} {emp.employee_lname}
              </Select.Option>
            ))}
          </Select>
        </Space>

        {currentModule?.right_add === "Y" && (
          <Button type="primary" onClick={showCreateModal}>
            Add New Issue - Timber
          </Button>
        )}
      </Space>

      <div
        style={{ background: colorBgContainer, borderRadius: borderRadiusLG }}
      >
        <Table<IssueMasterData>
          columns={columns}
          dataSource={data.slice(
            (pagination.current - 1) * pagination.pageSize,
            pagination.current * pagination.pageSize,
          )}
          rowKey="im_id"
          pagination={false}
          scroll={{ y: 500 }}
          summary={() => (
            <Table.Summary fixed="bottom">
              <Table.Summary.Row style={{ background: "#fafafa", fontWeight: 700 }}>
                <Table.Summary.Cell index={0} colSpan={3}>
                  Grand Total (Total Timber Issued Amount):
                </Table.Summary.Cell>
                <Table.Summary.Cell index={1}>
                  <Text strong style={{ fontSize: 15, color: "#52c41a" }}>
                    ₹{grandTotalAllTimber.toFixed(4)}
                  </Text>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={2} colSpan={(currentModule?.right_edit === "Y" || currentModule?.right_delete === "Y") ? 3 : 2} />
              </Table.Summary.Row>
            </Table.Summary>
          )}
          footer={() => (
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <Space size={16}>
                <Text>
                  Showing {(pagination.current - 1) * pagination.pageSize + 1}–
                  {Math.min(
                    pagination.current * pagination.pageSize,
                    data.length,
                  )}{" "}
                  of {data.length}
                </Text>
                <Text strong style={{ fontSize: 13, color: "#1677ff" }}>
                  Total Issued in Timber: ₹{grandTotalAllTimber.toFixed(4)}
                </Text>
              </Space>
              <Pagination
                current={pagination.current}
                pageSize={pagination.pageSize}
                total={data.length}
                showSizeChanger
                onChange={(page, pageSize) =>
                  setPagination({ current: page, pageSize })
                }
              />
            </div>
          )}
        />
      </div>

      <TimberIssueMasterModal
        visible={isModalVisible}
        onCancel={hideModal}
        onCreate={handleCreate}
        isEditMode={isEditMode}
        initialValues={initialValues || undefined}
      />
    </>
  );
};

export default TimberIssueMaster;
